import React, { useState, useEffect } from 'react';
import { Box, Typography, IconButton, Modal, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Button } from '@mui/material';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import FavoriteIcon from '@mui/icons-material/Favorite';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ProductDetails from './ProductDetails'; // Import your details component

export default function ProductCard({ product }) {
  const { imageUrl, discount, brand, title, link, price, originalPrice } = product;

  // Check if in giveaway mode
  const [isGiveawayMode, setIsGiveawayMode] = useState(false);

  // TODO: after unlike, just make the card dimmer incase it was an accident
  const [liked, setLiked] = useState(false); // State to track if the item is liked
  const [open, setOpen] = useState(false); // State to track if the modal is open
  const [tracking, setTracking] = useState(false); // State to track if price tracking is enabled
  const [emailDialogOpen, setEmailDialogOpen] = useState(false); // State for email prompt dialog
  const [email, setEmail] = useState('');

  // Function to check and update liked state from localStorage
  const refreshLikedState = () => {
    const likedItems = JSON.parse(localStorage.getItem('likedItems')) || [];
    const alreadyLiked = likedItems.some(item => item.title === title);
    setLiked(alreadyLiked);
  };

  // Function to check and update tracking state from localStorage
  const refreshTrackingState = () => {
    const trackedItems = JSON.parse(localStorage.getItem('trackedItems')) || [];
    const alreadyTracked = trackedItems.some(item => item.title === title);
    setTracking(alreadyTracked);
  };

  // Check localStorage for liked state on component mount
  useEffect(() => {
    refreshLikedState();
    refreshTrackingState();
    
    // Check if in giveaway mode
    const giveawayMode = localStorage.getItem('giveawayMode');
    setIsGiveawayMode(giveawayMode === 'true');

    // Listen for giveaway mode changes
    const handleGiveawayModeChange = () => {
      const giveawayMode = localStorage.getItem('giveawayMode');
      setIsGiveawayMode(giveawayMode === 'true');
    };

    window.addEventListener('storage', handleGiveawayModeChange);
    window.addEventListener('giveawayModeChanged', handleGiveawayModeChange);

    return () => {
      window.removeEventListener('storage', handleGiveawayModeChange);
      window.removeEventListener('giveawayModeChanged', handleGiveawayModeChange);
    };
  }, [title]);

  function addToLocalStorageArray(key, item) {
    const existing = JSON.parse(localStorage.getItem(key)) || [];
    // Avoid duplicates by id or title
    const alreadyExists = existing.some(i => i.id === item.id || i.title === item.title);
    if (!alreadyExists) {
      existing.push(item);
      localStorage.setItem(key, JSON.stringify(existing));
    }
  }
  

  const handleLike = (e) => {
    e.stopPropagation();
    const likedItems = JSON.parse(localStorage.getItem('likedItems')) || [];

    if (liked) {
      // Remove from liked by id or title
      const updatedLikedItems = likedItems.filter(
        (item) => item.id !== product.id && item.title !== product.title
      );
      localStorage.setItem('likedItems', JSON.stringify(updatedLikedItems));
      setLiked(false);
    } else {
      // Add to liked if not already present
      const alreadyExists = likedItems.some(
        (item) => item.id === product.id || item.title === product.title
      );
      if (!alreadyExists) {
        likedItems.push(product);
        localStorage.setItem('likedItems', JSON.stringify(likedItems));
      }
      setLiked(true);
    }
  };

  const handleTrackPrice = (e) => {
    e.stopPropagation();
    
    // Check if user email is in localStorage
    const userEmail = localStorage.getItem('userEmail') || localStorage.getItem('emailSubscribed');
    
    if (!userEmail) {
      // Prompt user for email
      setEmailDialogOpen(true);
    } else {
      // Toggle price tracking
      togglePriceTracking();
    }
  };

  const togglePriceTracking = async () => {
    const trackedItems = JSON.parse(localStorage.getItem('trackedItems')) || [];
    const userEmail = localStorage.getItem('userEmail') || localStorage.getItem('emailSubscribed');

    if (tracking) {
      // Remove from tracked
      const updatedTrackedItems = trackedItems.filter(
        (item) => item.id !== product.id && item.title !== product.title
      );
      localStorage.setItem('trackedItems', JSON.stringify(updatedTrackedItems));
      setTracking(false);

      // Optional: Call endpoint to remove tracking (if you want this functionality)
      // You can uncomment and configure this if needed
      /*
      try {
        await fetch('YOUR_ENDPOINT_URL/untrack', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            email: userEmail,
            productId: product.id || product.title,
          }),
        });
      } catch (error) {
        console.error('Failed to remove tracking:', error);
      }
      */
    } else {
      // Add to tracked if not already present
      const alreadyExists = trackedItems.some(
        (item) => item.id === product.id || item.title === product.title
      );
      if (!alreadyExists) {
        trackedItems.push({ ...product, trackedAt: new Date().toISOString() });
        localStorage.setItem('trackedItems', JSON.stringify(trackedItems));
      }
      setTracking(true);

      // Call endpoint to track price
      try {
        const response = await fetch('https://render-suq-backend.onrender.com/price-alert', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            email: userEmail,
            productId: product.id || product.title,
          }),
        });

        if (!response.ok) {
          console.error('Failed to start price tracking:', response.statusText);
        } else {
          console.log('Price tracking started successfully');
        }
      } catch (error) {
        console.error('Error starting price tracking:', error);
        // Optionally show user-friendly error message
        // alert('Failed to start price tracking. Please try again.');
      }
    }
  };

  const handleEmailSubmit = async () => {
    if (email && email.includes('@')) {
      // Store email in localStorage
      localStorage.setItem('userEmail', email);
      // Close dialog
      setEmailDialogOpen(false);
      // Reset email
      setEmail('');
      // Enable price tracking with API call
      await togglePriceTracking();
    } else {
      alert('Please enter a valid email address');
    }
  };

  const handleGiveawaySelection = () => {
    // Dispatch custom event with product data
    const event = new CustomEvent('giveawayProductSelected', {
      detail: product
    });
    window.dispatchEvent(event);
  };

  const handleCardClick = () => {
    // Always open the modal to view details
    setOpen(true);
  };

  return (
    <Box
      sx={{
        m: 0.5,
        mb: 1.3,
        ml: 1.3,
        width: '90%',
        overflow: 'hidden',
        position: 'relative',
        cursor: 'pointer',
        ...(isGiveawayMode && {
          border: '2px solid',
          borderColor: 'primary.main',
          borderRadius: 2,
        }),
      }}
    >
      {/* Discount Badge */}
      {discount && (
        <Box
          sx={{
            position: 'absolute',
            top: 8,
            left: 8,
            backgroundColor: 'rgba(141, 140, 140, 0.5)',
            color: 'white',
            borderRadius: 1,
            px: 1,
            py: 0.5,
            fontSize: '0.75rem',
            fontWeight: 'bold',
          }}
        >
          {discount}% off
        </Box>
      )}

      {/* Giveaway Mode Badge */}
      {isGiveawayMode && (
        <Box
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            backgroundColor: 'rgba(76, 175, 80, 0.9)',
            color: 'white',
            borderRadius: 1,
            px: 1,
            py: 0.5,
            fontSize: '0.75rem',
            fontWeight: 'bold',
          }}
        >
          Tap to Select
        </Box>
      )}

      {/* Product Image */}
      <Box
        component="img"
        src={imageUrl}
        alt={title}
        loading="lazy"
        sx={{
          width: '100%',
          aspectRatio: '1 / 1.4',
          objectFit: 'cover',
          borderRadius: 2,
          boxShadow: '1px 1px 10px rgba(0, 0, 0, 0.1)',
          cursor: 'pointer', // Show pointer cursor
        }}
        onClick={handleCardClick} // Open modal on click or select for giveaway
      />

      {/* Action Buttons */}
      <Box
        sx={{
          position: 'absolute',
          bottom: 72,
          right: 8,
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
        }}
      >
        <IconButton
          onClick={handleLike} // Handle like action
          sx={{
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
          }}
        >
          {liked ? (
            <FavoriteIcon sx={{ fontSize: 15, color: 'red' }} /> // Red heart when liked
          ) : (
            <FavoriteBorderIcon sx={{ fontSize: 15, color: 'white' }} /> // White outline when not liked
          )}
        </IconButton>
      </Box>

      {/* Product Details */}
        <Box
          sx={{
            p: 0.5,
            display: 'flex',
            flexDirection: 'column',
            mt: -1, // Moves the content closer to the image
          }}
        >
          <Typography
            variant="caption"
            sx={{
              color: 'gray',
              fontWeight: 'bold',
              fontFamily: 'Helvetica, Arial, sans-serif',
              textAlign: 'left',
            }}
          >
            {brand}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              fontFamily: 'Helvetica, Arial, sans-serif',
              lineHeight: 1.1,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              textAlign: 'left',
            }}
          >
            {title}
          </Typography>

          {/* Price */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'black' }}>
                ${price}
              </Typography>
              {/* {originalPrice && (
                <Typography
                  variant="body2"
                  sx={{
                    textDecoration: 'line-through',
                    color: 'gray',
                  }}
                >
                  ${originalPrice}
                </Typography>
              )} */}
            </Box>
            
            <IconButton
              onClick={handleTrackPrice} // Handle price tracking action
              size="small"
              sx={{
                backgroundColor: tracking ? 'rgba(255, 215, 0, 0.9)' : 'rgba(0, 0, 0, 0.7)',
                color: tracking ? 'black' : 'white',
                padding: '4px',
                '&:hover': {
                  backgroundColor: tracking ? 'rgba(255, 215, 0, 1)' : 'rgba(0, 0, 0, 0.8)',
                },
              }}
            >
              <VisibilityIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Box>
        </Box>
      

      {/* Product Details Modal */}
      <Modal open={open} onClose={() => { setOpen(false); refreshLikedState(); }} disableScrollLock={false}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            outline: 'none',
            width: '80%',
            maxHeight: '95vh',
            overflowY: 'auto',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <ProductDetails 
            product={product} 
            onClose={() => { setOpen(false); refreshLikedState(); }}
            onLikeChange={refreshLikedState}
            isGiveawayMode={isGiveawayMode}
            onGiveawaySelect={handleGiveawaySelection}
          />
        </Box>
      </Modal>

      {/* Email Prompt Dialog */}
      <Dialog 
        open={emailDialogOpen} 
        onClose={() => setEmailDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
            Track Price Changes
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
            Enter your email to get notified when the price of "{title}" drops!
          </Typography>
          <TextField
            fullWidth
            label="Enter your email address"
            variant="outlined"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                handleEmailSubmit();
              }
            }}
            sx={{
              mt: 1,
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
              },
            }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setEmailDialogOpen(false)} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={handleEmailSubmit}
            variant="contained"
            color="primary"
            disabled={!email.trim()}
            sx={{ borderRadius: 2 }}
          >
            Start Tracking
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}